import { RouteType } from "../../types/RouteType";
import { ServiceStore, RootStore } from "@intrasync/stores";
import MaterialsViewStore from "./MaterialsViewStore";

const MaterialsRoute: RouteType = {
  name: "Materials",
  path: "/materials/:tab?",
  defaultParams: { tab: "dashboard" },
  layout: "/app",
  requiresAuth: true,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./MaterialsHeader"),
      title: "Materials Management",
      enableRefreshButton: true,
    },
    body: {
      component: () => import("./MaterialsContainer"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }) => {
    // Load initial data for materials module - handle missing services gracefully
    try {
      await Promise.allSettled([
        (rootStore.materialsStore as ServiceStore)?.find({ query: { $limit: 50, active: true } }).catch(() => { }),
        (rootStore.materialLocationsStore as ServiceStore)?.find({ query: { $limit: 50, active: true } }).catch(() => { }),
        (rootStore.materialVendorsStore as ServiceStore)?.find({ query: { $limit: 50, active: true } }).catch(() => { }),
      ]);
    } catch (error) {
      console.warn("Materials services not available yet:", error);
    }
    return true;
  },
  onParamsChange: async ({ rootStore, params }) => {
    const { tab } = params;

    // Load tab-specific data - handle missing services gracefully
    try {
      switch (tab) {
        case "dashboard":
          // Load dashboard data including transactions for recent activity and inventory for stats
          await Promise.allSettled([
            (rootStore.materialTransactionsStore as ServiceStore)?.find({
              query: {
                $limit: 10
              }
            }).catch(() => { }),
            (rootStore.materialInventoryStore as ServiceStore)?.find({
              query: {
                $limit: 100
              }
            }).catch(() => { }),
            (rootStore.materialReorderRulesStore as ServiceStore)?.find({
              query: {
                $limit: 50,
                active: true
              }
            }).catch(() => { })
          ]);
          break;
        case "inventory":
          await (rootStore.materialInventoryStore as ServiceStore)?.find({
            query: {
              $limit: 50,
              status: { $in: ["available", "quarantine"] }
            }
          }).catch(() => { });
          break;
        case "transactions":
          await Promise.allSettled([
            (rootStore.materialTransactionsStore as ServiceStore)?.find({
              query: {
                $limit: 100
              }
            }).catch(() => { }),
            // Also load materials if not already loaded for transaction details
            (rootStore.materialsStore as ServiceStore)?.find({ 
              query: { 
                $limit: 100,
                active: true 
              } 
            }).catch(() => { }),
            (rootStore.materialLocationsStore as ServiceStore)?.find({ 
              query: { 
                $limit: 50,
                active: true 
              } 
            }).catch(() => { })
          ]);
          break;
        case "reorder":
          await (rootStore.materialReorderRulesStore as ServiceStore)?.find({
            query: {
              $limit: 50,
              active: true
            }
          }).catch(() => { });
          break;
        case "quality":
          await (rootStore.materialQualityCertsStore as ServiceStore)?.find({
            query: {
              $limit: 50
            }
          }).catch(() => { });
          break;
      }
    } catch (error) {
      console.warn("Tab-specific service not available:", error);
    }
  },
  dialogs: [
    {
      name: "MaterialForm",
      title: "Material",
      component: () => import("./dialogs/MaterialForm"),
      cancelButton: true,
      height: 600,
      onSubmit: async ({ rootStore, dialogContext }: { rootStore: RootStore; dialogContext?: string | number }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const material = materialsViewStore.currentDialogData;
        
        if (dialogContext && typeof dialogContext === "number") {
          // Edit mode - dialogContext is the material ID
          await (rootStore.materialsStore as ServiceStore).patch(dialogContext, material);
        } else {
          // Create mode
          await (rootStore.materialsStore as ServiceStore).create(material);
        }
        
        // Clear dialog data
        materialsViewStore.clearDialogData();
      },
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const material = materialsViewStore.currentDialogData;
        return !!(material?.materialCode && material?.name && material?.category && material?.unit);
      },
    },
    {
      name: "InventoryAdjustment",
      title: "Inventory Adjustment",
      component: () => import("./dialogs/InventoryAdjustmentForm"),
      cancelButton: true,
      height: 500,
      onSubmit: async ({ rootStore, dialogContext }: { rootStore: RootStore; dialogContext?: string | number }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const transaction = materialsViewStore.currentDialogData;
        
        // dialogContext could be materialId for pre-filled transactions
        if (dialogContext && typeof dialogContext === "number") {
          transaction.materialId = dialogContext;
        }
        
        await (rootStore.materialTransactionsStore as ServiceStore).create(transaction);
        // Refresh inventory after transaction
        await (rootStore.materialInventoryStore as ServiceStore).find({});
        
        // Clear dialog data
        materialsViewStore.clearDialogData();
      },
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const transaction = materialsViewStore.currentDialogData;
        return !!(transaction?.materialId && transaction?.quantity && transaction?.transactionType);
      },
    },
    {
      name: "VendorAssignment",
      title: "Vendor Assignment",
      component: () => import("./dialogs/VendorAssignmentForm"),
      cancelButton: true,
      height: 450,
      onSubmit: async ({ rootStore, dialogContext }: { rootStore: RootStore; dialogContext?: string | number }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const vendorAssignment = materialsViewStore.currentDialogData;
        
        if (dialogContext && typeof dialogContext === "number") {
          // Edit mode - dialogContext is the vendor assignment ID
          await (rootStore.materialVendorsStore as ServiceStore).patch(dialogContext, vendorAssignment);
        } else {
          // Create mode
          await (rootStore.materialVendorsStore as ServiceStore).create(vendorAssignment);
        }
        
        // Clear dialog data
        materialsViewStore.clearDialogData();
      },
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const vendorAssignment = materialsViewStore.currentDialogData;
        return !!(vendorAssignment?.materialId && vendorAssignment?.vendorId && vendorAssignment?.unitCost);
      },
    },
    {
      name: "ReorderRuleForm",
      title: "Reorder Rule",
      component: () => import("./dialogs/ReorderRuleForm"),
      cancelButton: true,
      height: 550,
      onSubmit: async ({ rootStore, dialogContext }: { rootStore: RootStore; dialogContext?: string | number }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const reorderRule = materialsViewStore.currentDialogData;
        
        if (dialogContext && typeof dialogContext === "number") {
          // Edit mode - dialogContext is the reorder rule ID
          await (rootStore.materialReorderRulesStore as ServiceStore).patch(dialogContext, reorderRule);
        } else {
          // Create mode
          await (rootStore.materialReorderRulesStore as ServiceStore).create(reorderRule);
        }
        
        // Clear dialog data
        materialsViewStore.clearDialogData();
      },
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const reorderRule = materialsViewStore.currentDialogData;
        return !!(
          reorderRule?.materialId &&
          reorderRule?.reorderPoint >= 0 &&
          reorderRule?.reorderQuantity > 0
        );
      },
    },
    {
      name: "QualityCertUpload",
      title: "Upload Quality Certificate",
      component: () => import("./dialogs/QualityCertUploadForm"),
      cancelButton: true,
      height: 500,
      onSubmit: async ({ rootStore, dialogContext }: { rootStore: RootStore; dialogContext?: string | number }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const qualityCert = materialsViewStore.currentDialogData;
        
        // dialogContext could be materialId for pre-filled certificates
        if (dialogContext && typeof dialogContext === "number") {
          qualityCert.materialId = dialogContext;
        }
        
        await (rootStore.materialQualityCertsStore as ServiceStore).create(qualityCert);
        
        // Clear dialog data
        materialsViewStore.clearDialogData();
      },
      onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
        const materialsViewStore = rootStore.materialsViewStore as MaterialsViewStore;
        const qualityCert = materialsViewStore.currentDialogData;
        return !!(
          qualityCert?.materialId &&
          qualityCert?.certType &&
          qualityCert?.certNumber &&
          qualityCert?.issuedBy
        );
      },
    },
  ],
};

export default MaterialsRoute;